import {EditorSelection, EditorState, type Extension, StateField, Transaction} from '@codemirror/state'
import type {Nodes} from 'mdast'
import {SKIP, visit} from 'unist-util-visit'

import {isPositionedNode} from '../../markdown/positioned-node'
import {setsEqual} from '../../utils'
import {clickingExtension, isClicking} from './clicking'
import {focusedExtension, isFocused} from './focused'
import {getMarkdownTree, markdownParserExtension, markdownTreeChanged} from './markdown-parser'

// --- Active blocks selection state tracking ---

function getFocusedSelection(state: EditorState) {
  return isFocused(state) ? state.selection : null
}

/**
 * Field that tracks selection state to use for calculating the current active node(s). This is similar to
 * `state.selection` but skips updates while the user is making a selection (while the mouse is down) and returns an
 * empty selection when the editor is not focused.
 */
const activeSelectionField = StateField.define<EditorSelection | null>({
  create: getFocusedSelection,
  update: (prev, tr) => (isClicking(tr.state) ? prev : getFocusedSelection(tr.state)),
  provide: () => [focusedExtension, clickingExtension],
})

function getActiveSelection(state: EditorState) {
  return state.field(activeSelectionField)
}

function activeSelectionChanged(transaction: Transaction) {
  const before = getActiveSelection(transaction.startState)
  const after = getActiveSelection(transaction.state)

  if (before === after) return false
  if (before === null || after === null) return true
  return !before.eq(after)
}

// --- Calculating the active nodes from selection state ---

function overlapsNode(selectionFrom: number, selectionTo: number, nodeFrom: number, nodeTo: number): boolean {
  return (
    (selectionFrom >= nodeFrom && selectionFrom < nodeTo) ||
    (selectionTo > nodeFrom && selectionTo <= nodeTo) ||
    (selectionFrom < nodeFrom && selectionTo > nodeTo)
  )
}

// Note: We could just expose this function as `getActiveNodes`, but that would offload the memoization and change
// tracking onto the consumer. Instead we store the active nodes on a state field for reuse when nothing changes.
function calculateActiveNodes(state: EditorState) {
  const selection = getActiveSelection(state)
  const tree = getMarkdownTree(state)

  const activeNodes = new Set<Nodes>()

  visit(tree, node => {
    if (!isPositionedNode(node)) return

    const overlapsSelection =
      selection?.ranges.some(range =>
        overlapsNode(range.from, range.to, node.position.start.offset, node.position.end.offset),
      ) ?? false

    if (overlapsSelection) activeNodes.add(node)
    else return SKIP // if this node doesn't overlap the selection, we know its children won't either so we can skip them
  })

  return activeNodes
}

/** Tracks the active node(s) in the editor, limiting recalculations to only when the editor or selection changes.  */
const activeNodesField = StateField.define<Set<Nodes>>({
  create: calculateActiveNodes,
  update: (prev, tr) => (activeSelectionChanged(tr) || markdownTreeChanged(tr) ? calculateActiveNodes(tr.state) : prev),
  provide: () => [activeSelectionField, markdownParserExtension],
})

/** Extension to calculate and track the set of active Markdown nodes in the editor. */
export const activeNodesExtension: Extension = activeNodesField

/** Get the set of active Markdown nodes */
export function getActiveNodes(state: EditorState) {
  return state.field(activeNodesField)
}

/** Check if the active nodes set changed. */
export function activeNodesChanged(transaction: Transaction) {
  const before = getActiveNodes(transaction.startState)
  const after = getActiveNodes(transaction.state)

  // Checking for set equality is fast and allows us to skip updates where the selection changes but the active nodes
  // doesn't. For example, when the user clicks around or arrow-key navigates within the same block.
  return !setsEqual(before, after)
}
